import { Check, Minus } from "lucide-react";
import { SectionContainer } from "@/components/marketing/SectionContainer";
import { PricingCard } from "@/components/marketing/PricingCard";
import { FaqItem } from "@/components/marketing/FaqItem";
import { CtaBand } from "@/components/marketing/CtaBand";
import { PRICING_TIERS, PRICING_MATRIX } from "@/app/marketing/constants/pricing";
import { PRICING_FAQ } from "@/app/marketing/constants/faq";
import { usePageMeta } from "./usePageMeta";

export function Pricing() {
  usePageMeta({
    title: "Pricing",
    description:
      "Flat per-agent pricing. Starter from $99/month, Growth at $499/month, Enterprise custom. Tenant isolation and approval gates on every tier.",
  });
  return (
    <>
      <SectionContainer>
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-brand-violet">Pricing</p>
          <h1 className="mt-3 font-display text-5xl md:text-6xl lg:text-7xl tracking-wide leading-[0.95] text-brand-ink">
            Simple. Per agent. No token math.
          </h1>
          <p className="mt-6 text-lg text-brand-ink/70 leading-relaxed">
            Pick a tier, pay monthly, cancel anytime. Every agent runs in a hard budget so you
            never get a surprise cloud bill.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {PRICING_TIERS.map((t) => (
            <PricingCard key={t.id} tier={t} />
          ))}
        </div>
      </SectionContainer>

      <SectionContainer className="border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-2xl">
          <h2 className="font-display text-4xl md:text-5xl tracking-wide text-brand-ink">
            What's in each tier.
          </h2>
          <p className="mt-3 text-base text-brand-ink/70">
            All three tiers ship with tenant isolation and approval gates. The difference is scale.
          </p>
        </div>

        <div className="mt-10 overflow-x-auto">
          <table className="w-full min-w-[640px] border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-brand-ink/50 py-3 px-4 border-b border-white/10">
                  Feature
                </th>
                {PRICING_TIERS.map((t) => (
                  <th
                    key={t.id}
                    className="text-center text-xs font-semibold uppercase tracking-wider text-brand-ink py-3 px-4 border-b border-white/10"
                  >
                    {t.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PRICING_MATRIX.map((row) => (
                <tr key={row.label} className="hover:bg-white/[0.02]">
                  <td className="text-sm text-brand-ink/85 py-3 px-4 border-b border-white/5">
                    {row.label}
                  </td>
                  <MatrixCell value={row.starter} />
                  <MatrixCell value={row.growth} />
                  <MatrixCell value={row.enterprise} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionContainer>

      <SectionContainer className="border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <div className="text-center">
            <p className="text-sm font-semibold uppercase tracking-wider text-brand-violet">
              Pricing FAQ
            </p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-wide text-brand-ink">
              Questions about billing.
            </h2>
          </div>
          <div className="mt-10 space-y-3">
            {PRICING_FAQ.map((item, i) => (
              <FaqItem key={i} item={item} />
            ))}
          </div>
        </div>
      </SectionContainer>

      <CtaBand />
    </>
  );
}

function MatrixCell({ value }: { value: string | boolean }) {
  if (value === true) {
    return (
      <td className="py-3 px-4 border-b border-white/5 text-center">
        <Check className="h-4 w-4 text-brand-violet mx-auto" aria-label="Included" />
      </td>
    );
  }
  if (value === false) {
    return (
      <td className="py-3 px-4 border-b border-white/5 text-center">
        <Minus className="h-4 w-4 text-brand-ink/30 mx-auto" aria-label="Not included" />
      </td>
    );
  }
  return (
    <td className="py-3 px-4 border-b border-white/5 text-center text-sm text-brand-ink/85">
      {value}
    </td>
  );
}
